import { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useCompanies, useUsers } from "./hooks";
import { assignNewFeatureToUser,getUserFeatures,getAssignedUserAccessFeatures,updateUserFeatureAccess,} from "./services/masterDataService";
import { useCurrentDomain } from "./context/DomainContext";

const BRAND_GREEN = "#0aa375";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    fontSize: "12px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, padding: "0 2px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 4px" }),
  clearIndicator: (base) => ({ ...base, padding: "0 4px" }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    backgroundColor: state.isSelected
      ? BRAND_GREEN
      : state.isFocused
      ? "#e6f7f2"
      : "white",
    color: state.isSelected ? "white" : "#333",
  }),
  singleValue: (base) => ({ ...base, fontSize: "12px" }),
  placeholder: (base) => ({ ...base, fontSize: "12px", color: "#adb5bd" }),
};

const Field = ({ label, required }) => (
  <div style={{ marginBottom: "4px" }}>
    <span
      style={{
        fontSize: "10px",
        fontWeight: 600,
        color: "#495057",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
      }}
    >
      {label}
      {required && (
        <span style={{ color: "#dc3545", marginLeft: "3px", fontSize: "14px", lineHeight: "1" }}>
          *
        </span>
      )}
    </span>
  </div>
);

const Spinner = () => (
  <div className="text-center py-4">
    <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);
const AddFeatureModal = ({ show, onClose, onSave, isSaving }) => {
  const [featureName, setFeatureName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setFeatureName("");
      setError("");
    }
  }, [show]);

  if (!show) return null;

  const handleSave = () => {
    const trimmed = featureName.trim();
    if (!trimmed) {
      setError("Feature name is required.");
      return;
    }
    setError("");
    onSave(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") onClose();
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1040,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1050,
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          width: "100%",
          maxWidth: "420px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: BRAND_GREEN,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>
            Add New Feature
          </span>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "18px",
              lineHeight: 1,
              cursor: "pointer",
              padding: "0 2px",
              opacity: 0.85,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "20px 16px 16px" }}>
          <div style={{ marginBottom: "14px" }}>
            <Field label="Feature Name" required />
            <input
              type="text"
              autoFocus
              value={featureName}
              onChange={(e) => {
                setFeatureName(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g. analytics_dashboard"
              className="form-control form-control-sm"
              style={{
                fontSize: "12px",
                borderColor: error ? "#dc3545" : "#dee2e6",
                outline: "none",
                boxShadow: "none",
              }}
            />
            {error && (
              <small style={{ color: "#dc3545", fontSize: "10px", marginTop: "3px", display: "block" }}>
                {error}
              </small>
            )}
          </div>
          <p style={{ fontSize: "11px", color: "#6c757d", margin: "0 0 16px" }}>
            This feature will be assigned to the selected user with{" "}
            <strong>Enabled</strong> status.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: "12px", minWidth: "70px" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-sm text-white fw-semibold"
              style={{
                backgroundColor: BRAND_GREEN,
                border: "none",
                fontSize: "12px",
                minWidth: "80px",
              }}
            >
              {isSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    style={{ width: "11px", height: "11px" }}
                    role="status"
                  />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const UserFeatureAccess = ({ activeTab }) => {
  const { domain } = useCurrentDomain();

  const [allFeatures, setAllFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featureSearch, setFeatureSearch] = useState("");

  const [selectedCompany, setSelectedCompany] = useState(null);
  const { companies, loading: companiesLoading } = useCompanies(domain);

  const [selectedUser, setSelectedUser] = useState(null);
  const { users } = useUsers(domain, selectedCompany?.value);

  const [assignedFeatures, setAssignedFeatures] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assigningFeature, setAssigningFeature] = useState(null);
  const [updatingFeature, setUpdatingFeature] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);

  useEffect(() => {
    if (!domain) return;
    setFeaturesLoading(true);
    getUserFeatures(domain)
      .then((data) => {
        setAllFeatures(data.map((item) => ({ label: item, value: item })));
      })
      .catch((err) => {
        console.error("Failed to load features:", err);
        toast.error("Failed to load features.");
      })
      .finally(() => setFeaturesLoading(false));
  }, [domain]);

  useEffect(() => {
    if (!domain || !selectedCompany?.value || !selectedUser?.value) {
      setAssignedFeatures([]);
      return;
    }
    fetchAssignedFeatures();
  }, [domain, selectedCompany?.value, selectedUser?.value]);

  const fetchAssignedFeatures = async () => {
    setAssignedLoading(true);
    try {
      const data = await getAssignedUserAccessFeatures(
        domain,
        selectedCompany.value,
        selectedUser.value
      );
      setAssignedFeatures(data || []);
    } catch (err) {
      console.error("Failed to load assigned features:", err);
      setAssignedFeatures([]);
    } finally {
      setAssignedLoading(false);
    }
  };

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected);
    setSelectedUser(null);
    setAssignedFeatures([]);
    setShowModal(false);
  };

  const handleUserChange = (selected) => {
    setSelectedUser(selected);
    setAssignedFeatures([]);
    setShowModal(false);
  };

  const isFeatureAssigned = (featureValue) =>
    assignedFeatures.some((f) => f.featureName === featureValue);

  const handleAssignFeature = async (feature) => {
    if (!selectedCompany || !selectedUser) {
      toast.warning("Please select a company and user first.");
      return;
    }
    if (isFeatureAssigned(feature.value)) return;

    setAssigningFeature(feature.value);
    const loadingToast = toast.loading(`Assigning "${feature.label}"...`);

    const payload = {
      userId: selectedUser.value,
      companyId: selectedCompany.value,
      featureName: feature.value,
      isEnabled: true,
      expiryDate: "2099-08-11T02:57:55.380Z",
      expiresInDays: 999,
      modifiedBy: sessionStorage.getItem("username"),
    };

    try {
      await assignNewFeatureToUser(domain, payload);
      toast.dismiss(loadingToast);
      toast.success(`"${feature.label}" assigned successfully.`);
      await fetchAssignedFeatures();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to assign feature."
      );
    } finally {
      setAssigningFeature(null);
    }
  };
  const handleModalSave = async (featureName) => {
    setModalSaving(true);
    const loadingToast = toast.loading(`Assigning "${featureName}"...`);

    const payload = {
      userId: selectedUser.value,
      companyId: selectedCompany.value,
      featureName: featureName,
      isEnabled: true,
      expiryDate: "2099-08-11T02:57:55.380Z",
      expiresInDays: 999,
      modifiedBy: sessionStorage.getItem("username"),
    };

    try {
      await assignNewFeatureToUser(domain, payload);
      toast.dismiss(loadingToast);
      toast.success(`"${featureName}" assigned successfully.`);
      if (!allFeatures.find((f) => f.value === featureName)) {
        setAllFeatures((prev) => [...prev, { label: featureName, value: featureName }]);
      }

      await fetchAssignedFeatures();
      setShowModal(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to assign feature."
      );
    } finally {
      setModalSaving(false);
    }
  };

  const handleToggleFeature = async (feature) => {
    if (!selectedCompany || !selectedUser || !feature.id) {
      toast.warning("Missing required information.");
      return;
    }

    setUpdatingFeature(feature.id);
    const loadingToast = toast.loading(`Updating "${feature.featureName}"...`);

    try {
      const payload = {
        domain,
        companyId: selectedCompany.value,
        userId: selectedUser.value,
        id: feature.id,
        isDisable: feature.isEnabled,
        modifiedBy: sessionStorage.getItem("username") || "Admin",
      };

      const result = await updateUserFeatureAccess(payload);
      toast.dismiss(loadingToast);

      if (result.Success || result.success) {
        toast.success(
          result.Message || result.message || `${feature.featureName} updated successfully.`
        );
        await fetchAssignedFeatures();
      } else {
        toast.error(result.Message || result.message || "Failed to update feature.");
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.message || "An error occurred while updating feature.");
    } finally {
      setUpdatingFeature(null);
    }
  };

const filteredFeatures = allFeatures
    .filter((f) => f.label.toLowerCase().includes(featureSearch.toLowerCase()))
    .sort((a, b) => {
        const aAssigned = isFeatureAssigned(a.value);
        const bAssigned = isFeatureAssigned(b.value);
        if (aAssigned !== bAssigned) return aAssigned ? 1 : -1;

        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    });
  const userAndCompanySelected = !!(selectedCompany && selectedUser);

  return (
    <>
      <AddFeatureModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleModalSave}
        isSaving={modalSaving}
      />
      <div className="container-fluid p-0 px-1">
        <div className="card-body">
          <div className="row g-2">
                        <div className="col-12 col-lg-7">
              <div className="card border h-100" style={{ borderRadius: "6px" }}>
                <div
                  className="card-header d-flex align-items-center justify-content-between"
                  style={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                    padding: "6px 10px",
                    backgroundColor: BRAND_GREEN,
                  }}
                >
                  <span>
                    Configured Features
                    {selectedUser && (
                      <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 400, opacity: 0.85 }}>
                        — {selectedUser.label}
                      </span>
                    )}
                  </span>                  
                </div>
                <div className="card-body p-2">
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <Field label="Company" required />
                      <Select
                        options={companies}
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        isLoading={companiesLoading}
                        placeholder="Select company..."
                        isSearchable
                        isClearable
                        styles={selectStyles}
                      />
                    </div>
                    <div className="col-6">
                      <Field label="User" required />
                      <Select
                        options={users}
                        value={selectedUser}
                        onChange={handleUserChange}
                        placeholder="Select user..."
                        isSearchable
                        isClearable
                        isDisabled={!selectedCompany}
                        styles={selectStyles}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      height: "300px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                    }}
                  >
                    {!selectedCompany || !selectedUser ? (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center h-100"
                        style={{ color: "#adb5bd", fontSize: "12px", gap: "6px" }}
                      >
                        <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#dee2e6" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.874M9 20H4v-2a4 4 0 015-3.874m6 5.874a4 4 0 10-8 0m8 0H9m4-8a4 4 0 11-8 0 4 4 0 018 0zm6-4a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>Select a company and user to view configured features</span>
                      </div>
                    ) : assignedLoading ? (
                      <Spinner />
                    ) : assignedFeatures.length === 0 ? (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center h-100"
                        style={{ color: "#adb5bd", fontSize: "12px", gap: "6px" }}
                      >
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#dee2e6" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span>No features configured yet.</span>
                        <span style={{ fontSize: "11px" }}>
                          Click a feature on the left or use "+ Add New Feature".
                        </span>
                      </div>
                    ) : (
                      <table className="table table-sm table-hover mb-0" style={{ fontSize: "12px" }}>
                        <thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
                          <tr>
                            <th style={{ padding: "6px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", color: "#6c757d", fontWeight: 600 }}>
                              Feature Name
                            </th>
                            <th style={{ padding: "6px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", color: "#6c757d", fontWeight: 600, textAlign: "center" }}>
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedFeatures.map((item, index) => (
                            <tr key={item.id || index}>
                              <td style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                                {item.featureName}
                              </td>
                              <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                <button
                                  className={`btn btn-sm ${item.isEnabled ? "btn-warning" : "btn-success"}`}
                                  onClick={() => handleToggleFeature(item)}
                                  disabled={updatingFeature === item.id}
                                  style={{ fontSize: "11px", padding: "3px 10px" }}
                                >
                                  {updatingFeature === item.id ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      style={{ width: "12px", height: "12px" }}
                                    />
                                  ) : item.isEnabled ? (
                                    "Click To Disable"
                                  ) : (
                                    "Click To Enable"
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {assignedFeatures.length > 0 && (
                    <div className="mt-1 text-end" style={{ fontSize: "10px", color: "#adb5bd" }}>
                      {assignedFeatures.length} feature(s) configured
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="card border h-100" style={{ borderRadius: "6px" }}>
                <div
                  className="card-header"
                  style={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                    padding: "8px 10px",
                    backgroundColor: BRAND_GREEN,
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    All Features
                    <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 400, opacity: 0.85 }}>
                      ({filteredFeatures.length})
                    </span>
                  </div>

                  {userAndCompanySelected && (
                    <button
                      onClick={() => setShowModal(true)}
                      className="btn btn-sm"
                      style={{
                        background: "rgba(255,255,255,0.15)",
                        border: "1px solid rgba(255,255,255,0.5)",
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "3px 10px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "rgba(255,255,255,0.28)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
                      }
                    >
                      <span style={{ fontSize: "15px", lineHeight: 1 }}>+</span>
                      Add New Feature
                    </button>
                  )}
                </div>
                <div className="card-body p-2">
                  <div className="mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search features..."
                      value={featureSearch}
                      onChange={(e) => setFeatureSearch(e.target.value)}
                      style={{ fontSize: "12px" }}
                    />
                  </div>
                  <div
                    style={{
                      height: "340px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      background: "#fafafa",
                    }}
                  >
                    {featuresLoading ? (
                      <Spinner />
                    ) : filteredFeatures.length === 0 ? (
                      <div className="text-center py-4" style={{ color: "#adb5bd", fontSize: "12px" }}>
                        No features found.
                      </div>
                    ) : (
                      filteredFeatures.map((feature) => {
                        const assigned = isFeatureAssigned(feature.value);
                        const loading = assigningFeature === feature.value;
                        return (
                          <div
                            key={feature.value}
                            onClick={() => !assigned && !loading && handleAssignFeature(feature)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "7px 10px",
                              borderBottom: "1px solid #f1f5f9",
                              cursor: assigned || loading ? "default" : "pointer",
                              fontSize: "12px",
                              opacity: assigned ? 0.45 : 1,
                              transition: "background 0.12s",
                              background: "transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!assigned && !loading)
                                e.currentTarget.style.background = "#e6f7f2";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <span style={{ flex: 1, color: "#212529" }}>{feature.label}</span>
                            {loading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                style={{ color: BRAND_GREEN, width: "12px", height: "12px", borderWidth: "2px" }}
                                role="status"
                              />
                            ) : assigned ? (
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  color: BRAND_GREEN,
                                  background: "#e6f7f2",
                                  padding: "1px 7px",
                                  borderRadius: "20px",
                                  border: `1px solid ${BRAND_GREEN}33`,
                                }}
                              >
                                ✓ Assigned
                              </span>
                            ) : (
                              <span style={{ fontSize: "10px", color: "#adb5bd", fontStyle: "italic" }}>
                                click to assign
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="mt-1 text-end" style={{ fontSize: "10px", color: "#adb5bd" }}>
                    {allFeatures.filter((f) => isFeatureAssigned(f.value)).length} of{" "}
                    {allFeatures.length} assigned
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserFeatureAccess;